import React, { useState, useEffect } from 'react';
import './App.css';
import './multiselect.css';
import './GummyAnalysis.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

// Import extracted components
import AppHeader from './components/AppHeader';
import AppFooter from './components/AppFooter';
import ProductListContainer from './components/ProductListContainer';
import AnalysisContainer from './components/AnalysisContainer';
import ProductDetail from './components/ProductDetail';
import { GummyMarketAnalysis } from './components/AnalysisComponents';
import ProductComparisonDashboard from './components/ProductComparisonDashboard';

// Import API utilities
import { fetchProducts, compareProducts } from './utils/api';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export default function App() {
    // Core state
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [comparisonData, setComparisonData] = useState(null);
    const [isCompareLoading, setIsCompareLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Analysis state
    const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);

    // Data fetching
    useEffect(() => {
        const loadProducts = async () => {
            try {
                const data = await fetchProducts();
                setProducts(data);
                setIsLoading(false);
            } catch (error) {
                setError(error);
                setIsLoading(false);
                console.error("Failed to fetch products:", error);
            }
        };

        loadProducts();
    }, []);

    // Event handlers
    const handleToggleSelect = (product) => {
        setSelectedProducts(prev => {
            const isSelected = prev.some(p => p.product_id === product.product_id);
            if (isSelected) {
                return prev.filter(p => p.product_id !== product.product_id);
            } else {
                if (prev.length >= 5) {
                    return prev;
                }
                return [...prev, product];
            }
        });
    };

    const handleViewDetails = (product) => {
        setSelectedProduct(product);
        window.scrollTo(0, 0);
    };

    const handleBack = () => {
        setSelectedProduct(null);
    };

    const clearSelection = () => {
        setSelectedProducts([]);
    };

    // Analysis handlers
    const handleAnalysisComplete = (analysis) => {
        setAiAnalysis(analysis);
        setIsAnalysisLoading(false);
    };

    const handleAnalysisError = (error) => {
        console.error('Analysis error:', error);
        alert('Analysis failed. Please try again.');
        setIsAnalysisLoading(false);
    };

    const handleCompare = async () => {
        if (selectedProducts.length < 2) {
            alert('Please select at least 2 products for comparison');
            return;
        }

        setIsCompareLoading(true);

        try {
            const comparisonResult = await compareProducts(selectedProducts);
            setComparisonData(comparisonResult);
            setIsCompareLoading(false);
        } catch (error) {
            console.error('Comparison error:', error);
            alert('Failed to load comparison. Please try again.');
            setIsCompareLoading(false);
        }
    };

    // Analysis handler - this will be passed to components that need it
    const handleDeepAnalysis = () => {
        setIsAnalysisLoading(true);
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>
                <h2>Loading BettrMatch Intelligence...</h2>
                <p>Analyzing product data and nutritional insights</p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="error-screen">
                <h2>Error Loading Products</h2>
                <p>Please check that the backend is running and accessible.</p>
            </div>
        );
    }

    return (
        <div className="app">
            <AppHeader products={products} />

            <main className="main-content">
                {isAnalysisLoading ? (
                    <AnalysisContainer
                        selectedProducts={selectedProducts}
                        onAnalysisComplete={handleAnalysisComplete}
                        onAnalysisError={handleAnalysisError}
                        isAnalysisLoading={isAnalysisLoading}
                        setIsAnalysisLoading={setIsAnalysisLoading}
                    />
                ) : isCompareLoading ? (
                    <div className="gummy-analysis-container">
                        {/* <div className="comparison-loading-overlay"> */}
                            <div className="comparison-loading-content">
                                <div className="comparison-loading-header">
                                    <div className="loading-spinner-large"></div>
                                    <h2>Comparing Products</h2>
                                    <p>Analyzing {selectedProducts.length} selected products and generating detailed comparison insights...</p>
                                </div>

                                <div className="selected-products-showcase">
                                    <h3>Products Being Compared</h3>
                                    <div className="comparison-products-grid">
                                        {selectedProducts.map((product, index) => (
                                            <div key={product.product_id} className="comparison-product-card">
                                                <div className="product-number-badge">{index + 1}</div>
                                                <div className="product-image-container">
                                                    <img
                                                        src={product.image_urls?.[0] || 'https://via.placeholder.com/120x120/f0f0f0/666666?text=No+Image'}
                                                        alt={product.product_name}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://via.placeholder.com/120x120/f0f0f0/666666?text=No+Image';
                                                        }}
                                                    />
                                                </div>
                                                <div className="product-info">
                                                    <h4>{product.product_name?.split(',')[0] || 'Unknown Product'}</h4>
                                                    <p className="brand">{product.extracted_brand_name || 'Unknown Brand'}</p>
                                                    <div className="product-score">
                                                        {product.bettr_score > 0 && (
                                                            <span className="score">Score: {product.bettr_score}%</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="comparison-loading-indicator">
                                                    <div className="mini-spinner"></div>
                                                    <span>Analyzing...</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="comparison-progress-info">
                                    <div className="progress-steps">
                                        <div className="step active">
                                            <span className="step-icon">📊</span>
                                            <span className="step-text">Collecting product data</span>
                                        </div>
                                        <div className="step active">
                                            <span className="step-icon">⚖️</span>
                                            <span className="step-text">Comparing specifications</span>
                                        </div>
                                        <div className="step">
                                            <span className="step-icon">🎯</span>
                                            <span className="step-text">Analyzing differences</span>
                                        </div>
                                        <div className="step">
                                            <span className="step-icon">📈</span>
                                            <span className="step-text">Generating insights</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        {/* </div> */}
                    </div>
                ) : comparisonData ? (
                    <div className="analysis-fullscreen-single">
                        <ProductComparisonDashboard
                            products={comparisonData}
                            onClose={() => setComparisonData(null)}
                        />
                    </div>
                ) : aiAnalysis ? (
                    <div className="analysis-fullscreen-single">
                        <GummyMarketAnalysis
                            analysis={aiAnalysis}
                            onClose={() => setAiAnalysis(null)}
                        />
                    </div>
                ) : (
                    <div className="container">
                        {selectedProduct ? (
                            <div className={`product-detail-layout ${selectedProducts.length > 0 ? 'with-selection' : 'full-width'}`}>
                                <div className="product-detail-section">
                                    <ProductDetail product={selectedProduct} onBack={handleBack} />
                                </div>
                                {selectedProducts.length > 0 && (
                                    <div className="selected-products-section">
                                        <ProductListContainer
                                            products={products}
                                            selectedProducts={selectedProducts}
                                            onToggleSelect={handleToggleSelect}
                                            onViewDetails={handleViewDetails}
                                            onDeepAnalysis={handleDeepAnalysis}
                                            onCompare={handleCompare}
                                            isAnalysisLoading={isAnalysisLoading}
                                            isCompareLoading={isCompareLoading}
                                            onClearSelection={clearSelection}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <ProductListContainer
                                products={products}
                                selectedProducts={selectedProducts}
                                onToggleSelect={handleToggleSelect}
                                onViewDetails={handleViewDetails}
                                onDeepAnalysis={handleDeepAnalysis}
                                onCompare={handleCompare}
                                isAnalysisLoading={isAnalysisLoading}
                                isCompareLoading={isCompareLoading}
                                onClearSelection={clearSelection}
                            />
                        )}
                    </div>
                )}
            </main>

            <AppFooter />
        </div>
    );
}
